import type { ImageBlock, ImagePlacement } from './types'

// An image block's size: `ImageBlock.width`, a whole-number percent of the
// text column (816px on desktop). Absent means the placement's default, and
// the image renders exactly as it did before widths existed. Stored values
// are clamped here at render, so switching placement never loses a width:
// column images (inline, full) take 30–100 and sit centred; wraps take 25–60
// and still float. See components/imageLayout.ts for how it's drawn.

export interface WidthRange {
  min: number
  max: number
  /** What an absent width means. */
  def: number
}

/** Inline and full-width images: centred in the column. */
export const COLUMN_WIDTHS: WidthRange = { min: 30, max: 100, def: 100 }
/**
 * Wraps: 31% of the column by default, which is what a default wrap draws
 * (250px at 816; components/imageLayout.ts). It was 37, so the corner and the
 * keys started a 250px wrap off from a width it didn't have and it jumped.
 */
export const WRAP_WIDTHS: WidthRange = { min: 25, max: 60, def: 31 }

/**
 * Below `md` a column image never shows narrower than this, so it stays
 * legible on a phone's 358px column (a 30% image would be ~107px wide).
 */
export const PHONE_MIN_WIDTH = 60

/** Sizes a drag settles on when it passes near them. */
export const SNAP_POINTS = [100, 75, 50, 33]
/** How near (in percent) a drag must pass to settle on a snap point. */
const SNAP_WITHIN = 2
/** One keyboard step (Alt+←/→, [ and ]). */
export const WIDTH_STEP = 5

export function isWrap(placement: ImagePlacement | undefined): boolean {
  return placement === 'wrap-left' || placement === 'wrap-right'
}

export function widthRange(placement: ImagePlacement | undefined): WidthRange {
  return isWrap(placement) ? WRAP_WIDTHS : COLUMN_WIDTHS
}

/** A width in whole percents, inside what the placement allows. */
export function clampWidth(placement: ImagePlacement | undefined, width: number): number {
  const r = widthRange(placement)
  if (!Number.isFinite(width)) return r.def
  return Math.min(r.max, Math.max(r.min, Math.round(width)))
}

/** A stored width, or null when it's absent or not a number (the default applies). */
function storedWidth(block: Pick<ImageBlock, 'width'>): number | null {
  const w = block.width
  return typeof w === 'number' && Number.isFinite(w) ? w : null
}

/** The percent of the column an image shows at on desktop: its width clamped, else the default. */
export function imageWidth(block: Pick<ImageBlock, 'placement' | 'width'>): number {
  const w = storedWidth(block)
  return w === null ? widthRange(block.placement).def : clampWidth(block.placement, w)
}

/**
 * The width when it differs from the placement's default, else null: then
 * the image renders with the classes it always had (so existing writings,
 * and any width that clamps back to the default, look exactly as before).
 */
export function customWidth(block: Pick<ImageBlock, 'placement' | 'width'>): number | null {
  const w = imageWidth(block)
  return w === widthRange(block.placement).def ? null : w
}

/**
 * The width as the corner handle is showing it mid-drag: inside the
 * placement's range but not rounded, so the image follows the pointer
 * smoothly (it settles on a whole percent when it's let go).
 */
export function liveWidth(block: Pick<ImageBlock, 'placement' | 'width'>): number {
  const w = storedWidth(block)
  const r = widthRange(block.placement)
  return w === null ? r.def : Math.min(r.max, Math.max(r.min, w))
}

/** Where a drag at `raw` percent settles: whole percents, pulled onto 100/75/50/33 when close, clamped. */
export function snapWidth(placement: ImagePlacement | undefined, raw: number): number {
  const r = widthRange(placement)
  const near = SNAP_POINTS.find((p) => p >= r.min && p <= r.max && Math.abs(raw - p) <= SNAP_WITHIN)
  return clampWidth(placement, near ?? raw)
}

// ── Drag to wrap (the image "hand", components/editor/useImageHand.ts) ──

/**
 * Where an image dragged to `x` lands: the column's left third wraps it left,
 * the right third wraps it right, the middle puts it back in the column (a
 * full-width image stays full). Edges count as the middle.
 */
export function placementAt(x: number, column: { left: number; width: number }, current: ImagePlacement | undefined): ImagePlacement {
  const third = column.width / 3
  if (x < column.left + third) return 'wrap-left'
  if (x > column.left + 2 * third) return 'wrap-right'
  return current === 'full' ? 'full' : 'inline'
}

/** Whether a press has moved far enough (more than `slop` px either way) to be a drag rather than a click. */
export function pastSlop(dx: number, dy: number, slop = 6): boolean {
  return Math.max(Math.abs(dx), Math.abs(dy)) > slop
}

/** The most lines down its paragraph a wrap can start (a stored offset is clamped to this). */
export const MAX_OFFSET = 60

/** A wrap's offset in whole lines (0 when absent, malformed, or the image isn't a wrap). */
export function wrapOffset(block: Pick<ImageBlock, 'placement' | 'offset'>): number {
  const o = block.offset
  if (!isWrap(block.placement) || typeof o !== 'number' || !Number.isFinite(o)) return 0
  return Math.min(MAX_OFFSET, Math.max(0, Math.round(o)))
}

/**
 * Somewhere the hand can put an image: before the column block `before`
 * (null = at the end), `offset` lines into it (wraps beside a paragraph
 * only), whose top would be at `top` (viewport px).
 */
export interface Slot {
  before: string | null
  offset: number
  top: number
}

/** How much nearer (px) another slot must be before the image leaves the one it's in, so it never flickers between two. */
const SLOT_STICK = 8

/**
 * The slot nearest `top` (where the dragged image's top edge is), staying in
 * `current` unless another is clearly nearer. Null when there are none.
 */
export function nearestSlot(slots: Slot[], top: number, current?: Pick<Slot, 'before' | 'offset'> | null): Slot | null {
  let best: Slot | null = null
  for (const s of slots) if (!best || Math.abs(s.top - top) < Math.abs(best.top - top)) best = s
  const here = current ? slots.find((s) => s.before === current.before && s.offset === current.offset) : undefined
  if (best && here && best !== here && Math.abs(here.top - top) - Math.abs(best.top - top) < SLOT_STICK) return here
  return best
}

const ORDER: ImagePlacement[] = ['wrap-left', 'inline', 'wrap-right']

/** One step left (−1) or right (+1) along wrap left · column · wrap right, stopping at the ends. */
export function stepPlacement(current: ImagePlacement | undefined, dir: -1 | 1): ImagePlacement {
  const at = ORDER.indexOf(current === 'full' || !current ? 'inline' : current)
  const next = ORDER[Math.min(ORDER.length - 1, Math.max(0, at + dir))]
  return next === 'inline' && current === 'full' ? 'full' : next
}
